/**
 * In-browser markdown editor with side-by-side chat (§15.4).
 *
 * Route: /projects/:projectId/editor?filename=<path>
 *
 * Left pane: CodeMirror 6 (markdown) — the buffer is the source of truth
 * while the user is editing; saves flush through the same pre-signed
 * upload flow as §7g.
 *
 * Right pane: a project-scoped chat against a chosen persona. Each chat
 * request sends the current editor buffer (`documentContent`) and the
 * filename (`documentFilename`) so the backend's `update_document_section`
 * tool can operate on unsaved edits. When the agent calls that tool, the
 * backend returns `{ newDocumentContent }` in the tool_result SSE event;
 * we apply the replacement as a single CodeMirror transaction so one
 * undo reverts it, and flip the dirty flag.
 *
 * Lock/unlock is a `.lock.json` sidecar in S3 — see §15.4.5 for the
 * rationale (avoids KB re-ingest churn on every toggle).
 *
 * Save is explicit: Ctrl/Cmd+S or the Save button. We deliberately don't
 * debounce-autosave — the chat panel doesn't see a dirty buffer, agent
 * edits compose correctly across turns without surprise rollbacks, and
 * users can undo back past an agent edit without fighting a background
 * write.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { EditorState, Compartment } from "@codemirror/state";
import { EditorView, keymap, lineNumbers } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { markdown } from "@codemirror/lang-markdown";
import { searchKeymap } from "@codemirror/search";

import Header from "../components/Header.jsx";
import Avatar from "../components/Avatar.jsx";
import ErrorBubble from "../components/ErrorBubble.jsx";
import Message from "../components/Message.jsx";
import ToolActivity from "../components/ToolActivity.jsx";
import PersonaPickerModal from "../components/PersonaPickerModal.jsx";
import {
  loadEditorDocument,
  lockDocument,
  unlockDocument,
  saveEditorDocument,
  generateAndDownload,
} from "../services/documents.js";
import { getProposal } from "../services/proposals.js";
import { streamChat } from "../services/chat.js";
import { useStore } from "../store/useStore.js";
import ReviewMode from "../components/editor/ReviewMode.jsx";
import CommentsSidebar from "../components/editor/CommentsSidebar.jsx";
import { COLLAB_MODE_ENABLED } from "../config.js";
import { getUserInfo } from "../services/auth.js";

// Mirrors backend doc-generator _AGENT_CONFIG (Phase 24). Generate button
// and preview toggle only show for documents whose filename matches the
// pipeline's agent-output naming convention AND whose agent ID is listed
// here — agents 0–5 (research/synthesis) have no associated template.
const GENERATABLE_AGENTS = {
  6: { format: "xlsx", label: "Generate XLSX" },
  7: { format: "pptx", label: "Generate PPTX" },
  8: { format: "pptx", label: "Generate PPTX" },
  9: { format: "pptx", label: "Generate PPTX" },
};

const AGENT_FILENAME_RE = /^agent-outputs\/research\/(\d+)_[^/]+\.md$/;
const PROPOSED_FILENAME_RE = /^agent-outputs\/research\/(\d+)_[^/]+\.proposed\.md$/;

function detectAgentIdFromFilename(filename) {
  const m = AGENT_FILENAME_RE.exec(filename || "");
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isFinite(id) ? id : null;
}

/** Derive the `.proposed.md` sidecar filename from a canonical `.md` path. */
function deriveProposedFilename(filename) {
  if (!filename || PROPOSED_FILENAME_RE.test(filename)) return null;
  const m = AGENT_FILENAME_RE.exec(filename);
  if (!m) return null;
  return filename.replace(/\.md$/, ".proposed.md");
}

export default function Editor() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filename = searchParams.get("filename") || "";

  const pushToast = useStore((s) => s.pushToast);
  const loadPersonas = useStore((s) => s.loadPersonas);
  const getPersonaById = useStore((s) => s.getPersonaById);

  // ── Proposal / review mode state ──
  const proposedFilename = useMemo(() => deriveProposedFilename(filename), [filename]);
  const [proposal, setProposal] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  // v4.1 Phase 38.4 — comments sidebar toggle (structured docs only).
  const [commentsOpen, setCommentsOpen] = useState(false);
  const callerSub = getUserInfo()?.sub;
  const isStructuredDoc =
    filename &&
    filename.endsWith(".md") &&
    !filename.startsWith("agent-outputs/research/");

  // ── Editor state ──
  const editorHostRef = useRef(null);
  const viewRef = useRef(null);
  const readOnlyCompartment = useRef(new Compartment()).current;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [content, setContent] = useState(""); // mirror of CodeMirror buffer for chat requests
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [locked, setLocked] = useState(false);
  const [lockedAt, setLockedAt] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Right pane mode: 'chat' for conversation with the GTM Agent, 'preview'
  // for the live slide/workbook approximation. Only surfaces for
  // generatable documents (agents 6–9); others stay locked to chat.
  const agentId = useMemo(
    () => detectAgentIdFromFilename(filename),
    [filename],
  );
  const genConfig = agentId != null ? GENERATABLE_AGENTS[agentId] : null;
  const isGeneratable = Boolean(genConfig);

  // Fresh ref for the chat panel to read current content without closing
  // over stale state.
  const contentRef = useRef("");
  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  // ── Chat panel state ──
  // Default to the GTM Agent — the editor is intended for iterating on
  // research/account-plan outputs, and that persona has the full toolset
  // (including update_document_section). User can "Change" to swap.
  const [personaId, setPersonaId] = useState("gtm-agent");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [chatError, setChatError] = useState(null);
  const streamAbortRef = useRef(null);

  useEffect(() => {
    loadPersonas();
  }, [loadPersonas]);

  const persona = personaId ? getPersonaById(personaId) : null;

  // ── Load document on mount ──
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!filename) {
        setLoadError("No filename specified.");
        setLoading(false);
        return;
      }
      try {
        const data = await loadEditorDocument(projectId, filename);
        if (cancelled) return;
        setContent(data.content ?? "");
        setLocked(!!data.locked);
        setLockedAt(data.lockedAt ?? null);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err.message);
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, filename]);

  // ── Check for a proposal sidecar ──
  useEffect(() => {
    if (!proposedFilename || loading) return;
    let cancelled = false;
    async function check() {
      try {
        const data = await getProposal(projectId, proposedFilename);
        if (!cancelled && data && data.status) setProposal(data);
      } catch {
        // 404 = no proposal — expected for most documents
      }
    }
    check();
    return () => { cancelled = true; };
  }, [projectId, proposedFilename, loading]);

  // ── Initialise CodeMirror once content is loaded ──
  useEffect(() => {
    if (loading || loadError || !editorHostRef.current) return;
    if (viewRef.current) return;

    const state = EditorState.create({
      doc: content,
      extensions: [
        lineNumbers(),
        history(),
        keymap.of([
          // Ctrl/Cmd+S is intercepted at the container level so it still
          // fires when focus is not on the editor (e.g. in the chat input).
          ...defaultKeymap,
          ...historyKeymap,
          ...searchKeymap,
        ]),
        markdown(),
        readOnlyCompartment.of(EditorState.readOnly.of(false)),
        EditorView.lineWrapping,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const next = update.state.doc.toString();
            setContent(next);
            setDirty(true);
          }
        }),
      ],
    });
    viewRef.current = new EditorView({
      state,
      parent: editorHostRef.current,
    });

    return () => {
      viewRef.current?.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadError]);

  // Apply read-only state when lock toggles.
  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: readOnlyCompartment.reconfigure(
        EditorState.readOnly.of(locked),
      ),
    });
  }, [locked, readOnlyCompartment]);

  // ── Save ──
  const handleSave = useCallback(async () => {
    if (!dirty || saving || locked) return;
    setSaving(true);
    try {
      const buffer = viewRef.current?.state.doc.toString() ?? content;
      await saveEditorDocument(projectId, filename, buffer);
      setDirty(false);
      setLastSavedAt(new Date());
      pushToast("Saved", "info");
    } catch (err) {
      pushToast(`Save failed: ${err.message}`, "error");
    } finally {
      setSaving(false);
    }
  }, [dirty, saving, locked, projectId, filename, content, pushToast]);

  // Global Ctrl/Cmd+S handler — fires even when focus is in the chat box.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleSave]);

  // ── Generate (PPTX / XLSX) ──
  const handleGenerate = useCallback(async () => {
    if (!genConfig || generating || !locked) return;
    setGenerating(true);
    try {
      await generateAndDownload(projectId, filename, genConfig.format);
      pushToast(`${genConfig.format.toUpperCase()} generated`, "info");
    } catch (err) {
      pushToast(`Generate failed: ${err.message}`, "error");
    } finally {
      setGenerating(false);
    }
  }, [genConfig, generating, locked, projectId, filename, pushToast]);


  // ── Lock / Unlock ──
  const handleToggleLock = async () => {
    setLockBusy(true);
    try {
      if (locked) {
        await unlockDocument(projectId, filename);
        setLocked(false);
        setLockedAt(null);
        pushToast("Document unlocked", "info");
      } else {
        if (dirty) {
          pushToast("Save your changes before locking.", "info");
          setLockBusy(false);
          return;
        }
        const resp = await lockDocument(projectId, filename);
        setLocked(true);
        setLockedAt(resp.lockedAt ?? new Date().toISOString());
        pushToast("Document locked", "info");
      }
    } catch (err) {
      pushToast(`Lock toggle failed: ${err.message}`, "error");
    } finally {
      setLockBusy(false);
    }
  };

  // ── Chat send ──
  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!input.trim() || streaming || !personaId) return;
    const userText = input.trim();
    setInput("");
    setChatError(null);

    setMessages((m) => [...m, { role: "user", content: userText }]);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    setStreaming(true);

    const abort = new AbortController();
    streamAbortRef.current = abort;

    try {
      await streamChat({
        personaId,
        projectId,
        message: userText,
        documentFilename: filename,
        documentContent: contentRef.current,
        signal: abort.signal,
        onToken: (tok) => {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last?.role === "assistant") {
              copy[copy.length - 1] = { ...last, content: last.content + tok };
            }
            return copy;
          });
        },
        onToolCall: (evt) => {
          setMessages((m) => [
            ...m,
            {
              role: "tool_activity",
              activities: [
                {
                  toolUseId: evt.toolUseId,
                  tool: evt.tool,
                  input: evt.input,
                  status: "running",
                },
              ],
            },
          ]);
        },
        onToolResult: (evt) => {
          // Apply update_document_section results to CodeMirror as a single
          // transaction so one undo reverts the edit.
          if (
            evt.tool === "update_document_section" &&
            evt.payload?.newDocumentContent != null &&
            viewRef.current
          ) {
            const next = evt.payload.newDocumentContent;
            viewRef.current.dispatch({
              changes: {
                from: 0,
                to: viewRef.current.state.doc.length,
                insert: next,
              },
            });
            setDirty(true);
          }
          setMessages((m) => {
            const copy = [...m];
            for (let i = copy.length - 1; i >= 0; i--) {
              const row = copy[i];
              if (row.role === "tool_activity") {
                const act = row.activities[0];
                if (act?.toolUseId === evt.toolUseId) {
                  copy[i] = {
                    ...row,
                    activities: [
                      { ...act, status: "done", summary: evt.summary },
                    ],
                  };
                  break;
                }
              }
            }
            // Start a fresh assistant bubble for any text that follows
            // the tool call.
            if (copy[copy.length - 1]?.role !== "assistant") {
              copy.push({ role: "assistant", content: "" });
            }
            return copy;
          });
        },
        onDone: () => {
          setStreaming(false);
        },
        onError: (err) => {
          setStreaming(false);
          setChatError(err.message);
        },
      });
    } catch (err) {
      if (err.name !== "AbortError") setChatError(err.message);
      setStreaming(false);
    }
  };

  useEffect(() => {
    return () => streamAbortRef.current?.abort();
  }, []);

  const statusLabel = useMemo(() => {
    if (saving) return "Saving…";
    if (dirty) return "Unsaved changes";
    if (lastSavedAt)
      return `Saved ${lastSavedAt.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    return "";
  }, [saving, dirty, lastSavedAt]);

  return (
    <div className="editor-page">
      <Header />
      <div className="editor-toolbar">
        <div className="editor-toolbar-left">
          <Link
            to={`/projects/${projectId}/documents`}
            className="editor-toolbar-back"
          >
            ← Documents
          </Link>
          <div className="editor-toolbar-filename">{filename}</div>
        </div>
        <div className="editor-toolbar-right">
          <span className="editor-toolbar-status">{statusLabel}</span>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleToggleLock}
            disabled={lockBusy || loading}
            title={
              locked
                ? `Locked${lockedAt ? ` at ${lockedAt}` : ""} — unlock to edit`
                : "Lock the document so it can be generated"
            }
          >
            {locked ? "Unlock" : "Lock"}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={!dirty || saving || locked}
          >
            Save
          </button>
          {isGeneratable && (
            <button
              type="button"
              className="btn-primary"
              onClick={handleGenerate}
              disabled={!locked || generating}
              title={locked ? `Generate ${genConfig.format.toUpperCase()}` : "Lock the document first to generate"}
            >
              {generating ? "Generating…" : genConfig.label}
            </button>
          )}
          {COLLAB_MODE_ENABLED && isStructuredDoc && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCommentsOpen((p) => !p)}
              title="Open comments sidebar"
            >
              Comments
            </button>
          )}
        </div>
      </div>
      {COLLAB_MODE_ENABLED && isStructuredDoc && (
        <CommentsSidebar
          projectId={projectId}
          filename={filename}
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          isOwner={false /* caller-owner signal lives on ProjectHeader; conservative default here */}
          callerSub={callerSub}
        />
      )}

      {proposal && !reviewOpen && (
        <div className="editor-proposal-banner">
          <span>
            Proposed changes from agent-{proposal.createdBy?.replace("agent-", "") ?? "?"} at{" "}
            {proposal.createdAt ? new Date(proposal.createdAt).toLocaleString() : "unknown time"}.
          </span>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={() => setReviewOpen(true)}
          >
            Review
          </button>
        </div>
      )}

      {reviewOpen && proposal && (
        <ReviewMode
          projectId={projectId}
          proposal={proposal}
          canonical={content}
          onClose={() => setReviewOpen(false)}
          onError={(msg) => pushToast(msg, "error")}
          onProposalUpdate={(updated) => {
            setProposal(updated);
            // If fully accepted, reload the canonical content in the editor
            if (updated.status === "accepted" && viewRef.current) {
              loadEditorDocument(projectId, filename)
                .then((data) => {
                  if (data.content != null && viewRef.current) {
                    viewRef.current.dispatch({
                      changes: {
                        from: 0,
                        to: viewRef.current.state.doc.length,
                        insert: data.content,
                      },
                    });
                    setContent(data.content);
                    setDirty(false);
                  }
                })
                .catch((err) => pushToast(`Failed to reload document: ${err.message}`, "error"));
            }
            // If fully resolved (accepted or rejected), clear proposal
            if (updated.status === "accepted" || updated.status === "rejected") {
              setProposal(null);
              setReviewOpen(false);
            }
          }}
        />
      )}

      {loadError ? (
        <ErrorBubble message={loadError} />
      ) : loading ? (
        <div className="editor-loading">Loading document…</div>
      ) : (
        <div className="editor-split">
          <div className="editor-pane editor-pane-code">
            <div ref={editorHostRef} className="editor-host" />
          </div>

          <div className="editor-pane editor-pane-chat">
            <>
                <div className="editor-chat-header">
                  {persona ? (
                    <>
                      <Avatar persona={persona} size="sm" />
                      <div className="editor-chat-persona-name">{persona.name}</div>
                      <button
                        type="button"
                        className="editor-chat-switch"
                        onClick={() => setPickerOpen(true)}
                      >
                        Change
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => setPickerOpen(true)}
                    >
                      Pick a coach
                    </button>
                  )}
                </div>

                <div className="editor-chat-thread">
                  {messages.length === 0 && !streaming && (
                    <div className="editor-chat-empty">
                      Ask the coach to review, rewrite a section, or draft new
                      content. They can edit this document directly — use the
                      Save button to persist changes.
                    </div>
                  )}
                  {messages.map((m, i) => {
                    if (m.role === "tool_activity") {
                      return <ToolActivity key={i} activities={m.activities} />;
                    }
                    return (
                      <Message
                        key={i}
                        role={m.role}
                        content={m.content}
                        persona={persona}
                      />
                    );
                  })}
                  {chatError && <ErrorBubble message={chatError} />}
                </div>

                <form className="editor-chat-form" onSubmit={handleSend}>
                  <textarea
                    className="editor-chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder={
                      persona
                        ? `Message ${persona.name}…`
                        : "Pick a coach to start the conversation"
                    }
                    disabled={streaming || !persona}
                    rows={2}
                  />
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={streaming || !persona || !input.trim()}
                  >
                    Send
                  </button>
                </form>
            </>
          </div>
        </div>
      )}

      <PersonaPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(p) => {
          setPersonaId(p.id);
          setPickerOpen(false);
        }}
        projectId={projectId}
      />
    </div>
  );
}
